import React, { useState, useEffect } from 'react';
import { ChevronUp, ChevronDown, Plus, Download } from 'lucide-react';
import api from '../api/axios';

import Sqids from 'sqids';
import { generateCompliantPassword, validatePassword, passwordPolicyHint } from '../utils/passwordPolicy';
import { escapeCsvCell } from '../utils/csv';

const TimeZoneList = ({ selectedTimeZone, onChange }) => {
  // You can use hardcoded arrays or full list from Intl
  const timezones = Intl.supportedValuesOf("timeZone");

  // Divide into regions
  const regions = {
    Africa: timezones.filter((tz) => tz.startsWith("Africa/")),
    America: timezones.filter((tz) => tz.startsWith("America/")),
    Asia: timezones.filter((tz) => tz.startsWith("Asia/")),
  };

  // Function to get correct UTC offset
  const getOffset = (timeZone) => {
    const now = new Date();
    
    // Get the local time string in the target timezone
    const tzString = now.toLocaleString("en-US", { 
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false
    });
    
    // Get the local time string in UTC
    const utcString = now.toLocaleString("en-US", { 
      timeZone: "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false
    });
    
    // Both interpreted as local time, so the difference is the offset
    const tzDate = new Date(tzString);
    const utcDate = new Date(utcString);
    
    const offsetMinutes = (tzDate.getTime() - utcDate.getTime()) / 60000;
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const absMinutes = Math.abs(offsetMinutes);
    const hours = String(Math.floor(absMinutes / 60)).padStart(2, "0");
    const minutes = String(absMinutes % 60).padStart(2, "0");
    
    return `${sign}${hours}:${minutes}`;
  };
  // Render options for a region
  // const renderRegionOptions = (regionName, tzArray) => (
  //   <>
  //     <option disabled style={{ fontWeight: "bold" }}>{regionName}</option>
  //     {tzArray.map((tz) => (
  //       <option key={tz} value={tz}>
  //         {getOffset(tz)} {tz}
  //       </option>
  //     ))}
  //   </>
  // );

  const renderRegionOptions = (regionName, tzArray) => {
    // Return an array instead of a fragment
    const options = [
      <option key={`${regionName}-header`} disabled style={{ fontWeight: "bold" }}>
        {regionName}
      </option>
    ];
    
    tzArray.forEach((tz) => {
      options.push(
        <option key={tz} value={tz}>
          {getOffset(tz)} {tz}
        </option>
      );
    });
    
    return options;
  };


  return (
    <select className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-md" value={selectedTimeZone} onChange={(e) => onChange(e.target.value)}>
      <option value="UTC">+00:00 UTC</option>
      {renderRegionOptions("Africa", regions.Africa)}
      {renderRegionOptions("America", regions.America)}
      {renderRegionOptions("Asia", regions.Asia)}
    </select>
  );
};

const OperatorTopUp = ({ selectedOperator, setSelectedOperator, setView}) => {

  const [transactions, setTransactions] = useState([]);
  const [numberOfReports, setNumberOfReports] = useState('');
  const [pricePerReport, setPricePerReport] = useState('');
  const [amtTopUp, setAmtTopUp] = useState('');
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const fetchTransactionHistory = async () => {
    if (selectedOperator?.operatorPk) {
      try {
        // setError(null); // Keep previous errors until a new action
        const response = await api.get('/webapi/operatortrxhistory', {
          params: { Operator_pk: selectedOperator.operatorPk }
        });
        setTransactions(response.data || []);
      } catch (err) {
        console.error('Error fetching transaction history:', err);
        setError('Failed to fetch transaction history.');
      }
    }
  }

  useEffect(() => {
    fetchTransactionHistory();
  }, [selectedOperator]);

  const handleSubmit = async () => {
    setError(null);
    setSuccessMessage(null);

    const payload = {
        operatorPk: selectedOperator.operatorPk,
        paymentType: selectedOperator.paymentType,
        currency: selectedOperator.currency,
        currencySymbol: selectedOperator.currencySymbol,
        rptTopUp: 0,
        pricePerReport: 0,
        amtTopUp: 0,
    };

    if (selectedOperator.paymentType === 1) {
        const rptTopUpValue = parseFloat(numberOfReports);
        if (isNaN(rptTopUpValue) || rptTopUpValue <= 0) {
          setError('Number of reports to top up must be a positive number.');
          return;
        }
        payload.rptTopUp = rptTopUpValue;
        payload.pricePerReport = parseFloat(pricePerReport) || 0;
    } else if (selectedOperator.paymentType === 2) {
        const amtTopUpValue = parseFloat(amtTopUp);
        if (isNaN(amtTopUpValue) || amtTopUpValue <= 0) {
          setError('Top up amount must be a positive number.');
          return;
        }
        payload.amtTopUp = amtTopUpValue;
    }
    
    try {
        await api.post('/webapi/operatortopup', payload);
        setSuccessMessage('Top-up successful!');
        // Clear form
        setNumberOfReports('');
        setPricePerReport('');
        setAmtTopUp('');
        // Refresh transactions
        fetchTransactionHistory();
    } catch (err) {
        console.error('Error submitting top-up:', err);
        setError(err.response?.data?.message || 'Failed to submit top-up.');
    }
  };

  return (
    <div className="min-h-screen ">
      <div className=" mx-auto ">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-gray-800 mb-2">Credit Top-up</h1>
          <div className="flex gap-4 text-sm">
          <button 
            onClick={() => setView('list')}
            className="text-indigo-600 hover:text-indigo-800"
          >
            Operator list
          </button>
          <span className="text-gray-400">/</span>
          <span className="text-gray-600">Transaction</span>
        </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-6" role="alert">
            <strong className="font-bold">Success: </strong>
            <span className="block sm:inline">{successMessage}</span>
          </div>
        )}

        {/* Account Top Up Card */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-1">Account Top Up</h2>
          <p className="text-gray-600 text-sm mb-6">{selectedOperator?.paymentType == 1 ? "Prepaid Account" : "Postpaid Account"}</p>

          {/* EYEAI Section */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
             
            <svg xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" viewBox="0 0 24 24">
                      <path d="M10.0813 3.7242C10.8849 2.16438 13.1151 2.16438 13.9187 3.7242V3.7242C14.4016 4.66147 15.4909 5.1127 16.4951 4.79139V4.79139C18.1663 4.25668 19.7433 5.83365 19.2086 7.50485V7.50485C18.8873 8.50905 19.3385 9.59842 20.2758 10.0813V10.0813C21.8356 10.8849 21.8356 13.1151 20.2758 13.9187V13.9187C19.3385 14.4016 18.8873 15.491 19.2086 16.4951V16.4951C19.7433 18.1663 18.1663 19.7433 16.4951 19.2086V19.2086C15.491 18.8873 14.4016 19.3385 13.9187 20.2758V20.2758C13.1151 21.8356 10.8849 21.8356 10.0813 20.2758V20.2758C9.59842 19.3385 8.50905 18.8873 7.50485 19.2086V19.2086C5.83365 19.7433 4.25668 18.1663 4.79139 16.4951V16.4951C5.1127 15.491 4.66147 14.4016 3.7242 13.9187V13.9187C2.16438 13.1151 2.16438 10.8849 3.7242 10.0813V10.0813C4.66147 9.59842 5.1127 8.50905 4.79139 7.50485V7.50485C4.25668 5.83365 5.83365 4.25668 7.50485 4.79139V4.79139C8.50905 5.1127 9.59842 4.66147 10.0813 3.7242V3.7242Z" fill="#00A3FF"></path>
                      <path class="permanent" d="M14.8563 9.1903C15.0606 8.94984 15.3771 8.9385 15.6175 9.14289C15.858 9.34728 15.8229 9.66433 15.6185 9.9048L11.863 14.6558C11.6554 14.9001 11.2876 14.9258 11.048 14.7128L8.47656 12.4271C8.24068 12.2174 8.21944 11.8563 8.42911 11.6204C8.63877 11.3845 8.99996 11.3633 9.23583 11.5729L11.3706 13.4705L14.8563 9.1903Z" fill="white"></path>
                    </svg>
                    <span className="font-medium text-gray-800">{selectedOperator?.company}</span>
            </div>

          </div>

          {/* Form Fields */}
          { selectedOperator?.paymentType == 1 && <>
            <div className="mb-6 ">
              <span className="text-gray-700">Available Report Count: </span>
              <span className="text-orange-500 font-bold text-2xl">{selectedOperator?.balance}</span>
            </div>
            
          <div className="space-y-6 mb-8">
            <div>
              <label className="block text-gray-700 text-sm mb-2">
                No. of Report to Top Up
              </label>
              <input
                type="text"
                autoComplete="off"
                value={numberOfReports}
                onChange={(e) => setNumberOfReports(e.target.value)}
                className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm mb-2">
                Price Per Report (Rate)
              </label>
              <input
                type="text"
                autoComplete="off"
                value={pricePerReport}
                onChange={(e) => setPricePerReport(e.target.value)}
                className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div></>
         }

        {/* Form Fields */}
        { selectedOperator?.paymentType == 2 && <>

          

          <div className="mb-6 ">
              <span className="text-gray-700">Credit Limit:  </span>
              <span className="text-orange-500 font-bold text-2xl">{selectedOperator?.currencySymbol}{selectedOperator?.creditLimit}</span>
            </div>
           <div className="mb-6 ">
              <span className="text-gray-700">Available Credit:  </span>
              <span className="text-orange-500 font-bold text-2xl">{selectedOperator?.currencySymbol}{selectedOperator?.balance}({selectedOperator?.creditLimit ? (selectedOperator?.balance/selectedOperator?.creditLimit*100).toFixed(2) : 0}%)</span>
            </div>

          <div className="space-y-6 mb-8">
            <div>
              <label className="block text-gray-700 text-sm mb-2">
              Top Up Amount ({selectedOperator?.currencySymbol})
              </label>
              <input
                id="Amt_TopUp" name="Amt_TopUp"
                type="text"
                autoComplete="off"
                value={amtTopUp}
                onChange={(e) => setAmtTopUp(e.target.value)}
                className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            
          </div></>
         }

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Submit
            </button>
            <button
              onClick={() => setView('list')}
              className="px-6 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Back
            </button>
          </div>
        </div>

        {/* Transaction History */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-6">Transaction History</h2>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Date</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Description</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">{selectedOperator?.paymentType == 1 ? "Report Count":"Amount"}</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700 text-sm">Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-800">{transaction.cUTCTime}</td>
                    <td className="py-3 px-4 text-sm text-gray-800">Account top up</td>
                    <td className="py-3 px-4 text-sm text-gray-800">{transaction.Credit}</td>
                    <td className="py-3 px-4 text-sm text-purple-700">Completed</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

}


  const OperatorDetail = ({ selectedOperator, setSelectedOperator, setView}) => {

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [selectedTimeZone, setSelectedTimeZone] = useState(selectedOperator.timeZone || 'Asia/Singapore');
    const handleGeneratePassword = () => {
      const generatedPassword = generateCompliantPassword();
      setPassword(generatedPassword);
      setConfirmPassword(generatedPassword);
      setError(null);
    };
    const handleSave = async () => {
      setError(null); // Clear previous errors
      setSuccessMessage(null);

      // Password validation
      if (!selectedOperator.operatorPk && !password) {
        setError('Password is required for a new operator.');
        return;
      }

      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }

      if (password) {
        const policyError = validatePassword(password);
        if (policyError) {
          setError(policyError);
          return;
        }
      }

      if (!selectedOperator.userId) {
        setError('Username is required.');
        return;
      }
      if (!selectedOperator.company) {
        setError('Company Name is required.');
        return;
      }
      if (!selectedOperator.userEmail) {
        setError('Email is required.');
        return;
      }
      if (!selectedOperator.firstName) {
        setError('First name is required.');
        return;
      }
      if (!selectedOperator.lastName) {
        setError('Last name is required.');
        return;
      }
      if (!selectedOperator.address1) {
        setError('Address #1 is required.');
        return;
      }

      const payload = {
        userPk: selectedOperator.userPk,
        userId: selectedOperator.userId,
        active: selectedOperator.active,
        userLocked: selectedOperator.userLocked,
        accExpire: selectedOperator.accExpire,
        accExpiryDateTime: selectedOperator.accExpiryDateTime,
        userPwd: password,
        userCPwd: confirmPassword,
        firstname: selectedOperator.firstName,
        lastname: selectedOperator.lastName,
        userEmail: selectedOperator.userEmail,
        timeZone: selectedTimeZone,
        operatorPk: selectedOperator.operatorPk,
        companyName: selectedOperator.company,
        ctryCode: selectedOperator.ctryCode,
        terrority: selectedOperator.terrority,
        billCycle: selectedOperator.billCycle,
        paymentType: selectedOperator.paymentType,
        currency: selectedOperator.currency,
        currencySymbol: selectedOperator.currencySymbol,
        pricePerReport: selectedOperator.pricePerReport,
        creditLimit: selectedOperator.creditLimit,
        contact: selectedOperator.contact,
        address1: selectedOperator.address1,
        address2: selectedOperator.address2,
        city: selectedOperator.city,
        state: selectedOperator.state,
        postalCode: selectedOperator.postalCode,
        country: selectedOperator.country,
      };
      try {
        let response;
        if (selectedOperator.operatorPk && selectedOperator.userPk) {
          response = await api.put('/webapi/operators', payload);
          setSuccessMessage('Operator details updated successfully.');
        } else {
          response = await api.post('/webapi/operators', payload);
          setSuccessMessage('New operator added successfully.');
          // Update the selectedOperator with the new Pks from the response
          setSelectedOperator(prev => ({ ...prev, ...response.data, paymentType: prev.paymentType }));
        }
      } catch (err) {
        console.error('Error saving operator:', err);
        const apiError = err.response?.data?.error || err.response?.data?.message || 'Failed to save operator details. Please try again.';
        setError(apiError);
      }
      const el = document.querySelector('main');
      if (el) {
        el.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };
    return (
    <div className="min-h-screen " id="main-div">
      <div >
        <div className="mb-6">
          <h1 className="text-3xl font-semibold text-gray-800 mb-1">Operator</h1>
          <div className="flex gap-4 text-sm">
            <button 
              onClick={() => setView('list')}
              className="text-indigo-600 hover:text-indigo-800"
            >
              Operator list
            </button>
            <span className="text-gray-400">/</span>
            <span className="text-gray-600">Operator Profile</span>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-6" role="alert">
            <strong className="font-bold">Success: </strong>
            <span className="block sm:inline">{successMessage}</span>
          </div>
        )}


        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-6">Login Access</h2>
            
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-2">Username</label>
              <input 
                type="text"
                autoComplete="off"
                value={selectedOperator?.userId ?? ''}
                onChange={(e) =>
                  setSelectedOperator(prev => ({ ...prev, userId: e.target.value }))
                }
                className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <div className="flex items-center justify-between mb-2 gap-3">
                  <label className="block text-sm text-gray-600">Password</label>
                  <button
                    type="button"
                    onClick={handleGeneratePassword}
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
                  >
                    Generate
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="off"
                    className="w-full px-4 py-2 pr-10 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-2">Confirm password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="off"
                    className="w-full px-4 py-2 pr-10 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-500 mb-4">
              {passwordPolicyHint}
            </p>

            <div className="mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedOperator?.active === 'Y'}
                  onChange={(e) =>
                    setSelectedOperator(prev => ({ ...prev, active: e.target.checked ? 'Y' : 'N' }))}
                  className="w-4 h-4 text-indigo-600"
                />
                <span className="text-sm text-gray-700">Active - Account status</span>
              </label>
            </div>

            <div className="mb-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedOperator?.userLocked === 1}
                  onChange={(e) =>
                    setSelectedOperator(prev => ({ ...prev, userLocked: e.target.checked ? 1 : 0 }))}
                  className="w-4 h-4 text-red-600"
                />
                <span className="text-sm text-gray-700">Locked - Account locked</span>
              </label>
            </div>

            <div className="mb-4">
              <label className="flex items-center gap-2 text-sm text-gray-700 mb-2">
                <input
                  type="checkbox"
                  checked={selectedOperator?.accExpire === 'Y'}
                  onChange={(e) =>
                    setSelectedOperator(prev => ({ ...prev, accExpire: e.target.checked ? 'Y' : 'N' }))}
                  className="w-4 h-4"
                />
                Account expires
              </label>
              <input
                type="date"
                placeholder="Expiry date"
                className="px-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                value={selectedOperator?.accExpiryDateTime ?? ''}
                onChange={(e) =>
                  setSelectedOperator(prev => ({ ...prev, accExpiryDateTime: e.target.value }))
                }
              />
            </div>

            <div className="h-1 mb-4 bg-[radial-gradient(circle_at_center,theme(colors.red.300)_1px,transparent_1px)] [background-size:6px_6px] bg-repeat-x"></div>


            <h3 className="text-lg font-semibold text-gray-800 mb-6">Info and Preference</h3>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm text-gray-600 mb-2">First name</label>
                <input 
                  type="text" 
                  autoComplete="off"
                  className="w-full px-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  onChange={(e) =>
                    setSelectedOperator(prev => ({ ...prev, firstName: e.target.value }))
                  }
                  value={selectedOperator?.firstName || ''}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-2">Last name / surname</label>
                <input 
                  type="text" 
                  autoComplete="off"
                  value={selectedOperator?.lastName ?? ''}
                  onChange={(e) =>
                    setSelectedOperator(prev => ({ ...prev, lastName: e.target.value }))}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm text-gray-600 mb-2">New email address</label>
                <input 
                  type="email" 
                  value={selectedOperator?.userEmail ?? ''}
                  onChange={(e) =>
                    setSelectedOperator(prev => ({ ...prev, userEmail: e.target.value }))}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-2">Contact</label>
                <input 
                  type="text" 
                  autoComplete="off"
                  value={selectedOperator?.contact ?? ''}
                  onChange={(e) =>
                    setSelectedOperator(prev => ({ ...prev, contact: e.target.value }))}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-2">Time Zone</label>
              {/* <select className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-md">
                <option>{selectedOperator?.timezone || '+08:00 Asia/Singapore'}</option>
              </select> */}
              <TimeZoneList
                  selectedTimeZone={selectedTimeZone}
                  onChange={setSelectedTimeZone} // updates state automatically
                />
                
            </div>

            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-2">Company Name <span className="text-xs text-blue-500">(required)</span></label>
              <input 
                type="text"
                autoComplete="off"
                value={selectedOperator?.company || ''}
                onChange={(e) =>
                  setSelectedOperator(prev => ({
                    ...prev,
                    company: e.target.value
                  }))
                }
                className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-2">Address #1 <span className="text-xs text-blue-500">(required)</span></label>
              <input 
                type="text"
                autoComplete="off"
                value={selectedOperator?.address1 ?? ''}
                onChange={(e) =>
                  setSelectedOperator(prev => ({ ...prev, address1: e.target.value }))}
                className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-2">Address #2</label>
              <input 
                type="text"
                autoComplete="off"
                className="w-full px-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                value={selectedOperator?.address2 ?? ''}
                onChange={(e) =>
                  setSelectedOperator(prev => ({ ...prev, address2: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm text-gray-600 mb-2">Country</label>
                <select
                  value={selectedOperator?.country || ''}
                  onChange={(e) =>
                    setSelectedOperator(prev => ({ ...prev, country: e.target.value }))}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="Australia">Australia</option><option value="China">China</option><option value="Hong Kong">Hong Kong</option><option value="Indonesia">Indonesia</option><option value="Malaysia">Malaysia</option><option value="Myanmar">Myanmar</option><option value="Philippines">Philippines</option><option value="Singapore" >Singapore</option><option value="Thailand">Thailand</option><option value="Vietnam">Vietnam</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-2">State</label>
                <input 
                  type="text"
                  autoComplete="off"
                  className="w-full px-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={selectedOperator?.state ?? ''}
                  onChange={(e) =>
                    setSelectedOperator(prev => ({ ...prev, state: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm text-gray-600 mb-2">City/Town</label>
                <input
                  type="text" 
                  autoComplete="off"
                  className="w-full px-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={selectedOperator?.city ?? ''}
                  onChange={(e) =>
                    setSelectedOperator(prev => ({ ...prev, city: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-2">Zip / Postal Code</label>
                <input 
                  type="text"
                  autoComplete="off"
                  value={selectedOperator?.postalCode ?? ''}
                  onChange={(e) =>
                    setSelectedOperator(prev => ({ ...prev, postalCode: e.target.value }))}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-6">Billing Cycle</h2>
            
            <p className="text-sm text-gray-600 mb-4">Set the billing cycle for this account.</p>

            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-2">Billing</label>
              <select
                value={selectedOperator?.billCycle || 'WEEKLY'}
                onChange={(e) =>
                  setSelectedOperator(prev => ({ ...prev, billCycle: e.target.value }))}
                className=" px-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option>WEEKLY</option>
                <option>MONTHLY</option>
                <option>QUARTERLY</option>
                <option>HALFYEARLY</option>
                <option>YEARLY</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm text-gray-600 mb-2">Currency</label>
                <select
                  value={selectedOperator?.currency}
                  onChange={(e) =>
                    setSelectedOperator(prev => ({ ...prev, currency: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option>AUD</option>
                  <option>JPY</option>
                  <option>MYR</option>
                  <option>PHP</option>
                  <option>RMB</option>
                  <option>SGD</option>
                  <option>THB</option>
                  <option>USD</option>
                  
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-2">Currency Symbol</label>
                <input 
                  type="text"
                  autoComplete="off"
                  value={selectedOperator?.currencySymbol}
                  onChange={(e) =>
                    setSelectedOperator(prev => ({
                      ...prev,
                      currencySymbol: e.target.value
                    }))
                  }
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="mb-6">
              <p className="text-sm font-medium text-pink-600 mb-2">Charging Type:</p>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  disabled={!!selectedOperator?.operatorPk}
                  name="charging"
                  checked={selectedOperator?.paymentType === 1}
                  onChange={() =>
                    setSelectedOperator(prev => ({ ...prev, paymentType: 1 }))}
                  className="w-4 h-4 text-indigo-600"
                />
                <span className="text-sm text-gray-700">Pre-paid</span>
              </label>
            </div>
            
            { (
              <>
                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-2">Report count:</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="text-3xl font-semibold text-green-600">
                        {selectedOperator?.balance || 0}
                      </div>
                      <div className="text-sm text-gray-500">Available</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-semibold text-gray-800">
                        {selectedOperator?.pricePerReport || ''}
                      </div>
                      <div className="text-sm text-gray-500">Price Per Report</div>
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="mb-6">
              <label className="flex items-center gap-2 text-sm text-gray-500">
                <input
                  type="radio"
                  disabled={!!selectedOperator?.operatorPk}
                  name="charging"
                  className="w-4 h-4"
                  checked={selectedOperator?.paymentType === 2}
                  onChange={() =>
                    setSelectedOperator(prev => ({ ...prev, paymentType: 2 }))}
                />
                Post-paid
              </label>
            </div>

            { (
              <>
                <div className="mb-6">
                  <label className="block text-sm text-gray-600 mb-2">Price Per Report</label>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">$</span>
                    <input
                      type="text" 
                      autoComplete="off"
                      className="flex-1 px-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      value={selectedOperator?.pricePerReport ?? ''}
                      onChange={(e) =>
                        setSelectedOperator(prev => ({ ...prev, pricePerReport: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="mb-6">
                  <label className="block text-sm text-gray-600 mb-2">Maximum Credit Limit</label>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600">$</span>
                    <input
                      type="text" 
                      autoComplete="off"
                      value={selectedOperator?.creditLimit || ''}
                      onChange={(e) =>
                        setSelectedOperator(prev => ({
                          ...prev,
                          creditLimit: e.target.value
                        }))
                      }
                      className="flex-1 px-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <p className="text-sm font-medium text-orange-600 mb-4">Country and Terrority for this account:</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-2">Country</label>
                  <select
                    value={selectedOperator?.ctryCode || 'Singapore'}
                    onChange={(e) => setSelectedOperator({...selectedOperator, ctryCode: e.target.value})}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="AU">Australia</option><option value="CN">China</option><option value="HK">Hong Kong</option><option value="IN">Indonesia</option><option value="MY">Malaysia</option><option value="MM">Myanmar</option><option value="PH">Philippines</option><option value="SG" >Singapore</option><option value="TH">Thailand</option><option value="VN">Vietnam</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-2">Terrority</label>
                  <input 
                    type="text"
                    autoComplete="off"
                    value={selectedOperator?.territory || ''}
                    onChange={(e) =>
                      setSelectedOperator(prev => ({
                        ...prev,
                        territory: e.target.value
                      }))
                    }
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-4 mt-6">
          <button 
            onClick={handleSave}
            className="bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700"
          >
            Save
          </button>
          <button 
            onClick={() => setView('list')}
            className="bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700"
          >
            Back
          </button>
        </div>
      </div>
    </div>
  )};



const OperatorsManagement = () => {
  const [view, setView] = useState('list');
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [operators, setOperators] = useState([]);
  const [error, setError] = useState(null);
  const sqids = new Sqids({
    alphabet: 'abcdefghijklmnopqrstuvwxyz@#%$&*0123456789',
    minLength: 10
  });

  useEffect(() => {
    const fetchOperators = async () => {
      try {
        setError(null);
        const response = await api.get('/webapi/operators');
        if (response.data) {
          // const formattedOperators = response.data.operators.map(op => {
          //   const [country, territory] = op.countryTerritory ? op.countryTerritory.split(', ') : ['', ''];
          //   return {...op,
          //     // id: op.operatorPk,
          //     // userPk: op.userPk,
          //     // username: op.userId,
          //     // name: op.fullName,
          //     // company: op.company,
          //     // email: op.userEmail,
          //     //country: country,
          //     //territory: territory,
          //     // reportAvailable: op.balance, // Assuming balance is reportAvailable
          //     // availableBal: op.balance,
          //     // limit: op.creditLimit,
          //     // status: op.status,
          //     // statusHighlight: op.statusHighlight,
          //     // paymentType: op.paymentType,
          //     // currencySymbol: op.currencySymbol,
          //   };
          // });
          setOperators(response.data.operators);
        }
      } catch (err) {
        console.error('Error fetching operators:', err);
        setError('Failed to fetch operator list. Please try again later.');
        setOperators([]);
      }
    };

    if (view === 'list') {
      fetchOperators();
    }
  }, [view]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedOperators = [...operators].sort((a, b) => {
    if (sortConfig.key) {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronUp className="w-4 h-4 text-gray-300" />;
    }
    return sortConfig.direction === 'asc' ? 
      <ChevronUp className="w-4 h-4 text-gray-600" /> : 
      <ChevronDown className="w-4 h-4 text-gray-600" />;
  };

  const handleOperateClick = (operator) => {
    setSelectedOperator(operator );
    setView('detail');
  };

  const handleDownloadCSV = () => {
    if (!operators || operators.length === 0) {
      setError('No operators to download.');
      return;
    }

    const headers = ['Username', 'Name', 'Company', 'Email', 'Country', 'Territory', 'Balance', 'Credit Limit', 'Status'];
    const csvContent = [
      headers.join(','),
      ...sortedOperators.map(op => {
        return [
          escapeCsvCell(op.userId),
          escapeCsvCell(`${op.firstName || ''} ${op.lastName || ''}`.trim()),
          escapeCsvCell(op.company),
          escapeCsvCell(op.userEmail),
          escapeCsvCell(op.ctryCode),
          escapeCsvCell(op.terrority),
          escapeCsvCell(op.balance || 0),
          escapeCsvCell(op.creditLimit || 0),
          escapeCsvCell(op.status)
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `operators_list_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const OperatorsList = () => (
    <div className="min-h-screen ">
      <div >
        <div className="mb-6">
          <h1 className="text-3xl font-semibold text-gray-800 mb-1">Operators</h1>
          <p className="text-gray-500">Account list</p>
        </div>
        {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
              <strong className="font-bold">Error: </strong>
              <span className="block sm:inline">{error}</span>
            </div>
          )}

        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <p className="text-sm text-gray-500 mb-1">Registered Operators</p>
          <p className="text-3xl font-semibold text-gray-800">{operators.length}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex gap-4 mb-6">
            <button 
            onClick={() => {
              setSelectedOperator({
                username: '',
                name: '',
                company: '',
                email: '',
                country: 'Singapore',
                territory: 'SG',
                billCycle: 'WEEKLY',
                currency: 'SGD',
                operatorPk: '',
                userPk: '',
                currencySymbol: '$',
                chargingType: 'Pre-paid',
                pricePerReport: 10.00,
                maxCreditLimit: 1000.00,
                status: 'Active'
              });
              setView('detail');
            }}
            className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Account
          </button>
          <button
            onClick={handleDownloadCSV}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            CSV
          </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                    <button onClick={() => handleSort('name')} className="flex items-center gap-1 hover:text-gray-900">
                      USER <SortIcon columnKey="name" />
                    </button>
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                    <button onClick={() => handleSort('country')} className="flex items-center gap-1 hover:text-gray-900">
                      Country / territory <SortIcon columnKey="country" />
                    </button>
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                    <button onClick={() => handleSort('reportAvailable')} className="flex items-center gap-1 hover:text-gray-900">
                      Report Available <SortIcon columnKey="reportAvailable" />
                    </button>
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                    <button onClick={() => handleSort('availableBal')} className="flex items-center gap-1 hover:text-gray-900">
                      Available Bal / Limit <SortIcon columnKey="availableBal" />
                    </button>
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                    <button onClick={() => handleSort('status')} className="flex items-center gap-1 hover:text-gray-900">
                      Status <SortIcon columnKey="status" />
                    </button>
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Action</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">QR Code</th>
                </tr>
              </thead>
              <tbody>
                {sortedOperators.map((operator,index) => (
                  <tr 
                    key={index} 
                    className="border-b border-gray-100 hover:bg-gray-50 "
                    
                  >
                    <td className="py-4 px-4 cursor-pointer"
                
                    onClick={() => handleOperateClick(operator)}

                    >
                      <div className="font-medium text-gray-800">{operator.firstName +' '+ operator.lastName }</div>
                      <div className="text-sm text-gray-500">{operator.company}</div>
                      <div className="text-sm text-gray-500">{operator.userEmail}</div>
                    </td>
                    <td className="py-4 px-4 text-gray-700">
                      {operator.ctryCode}, {operator.terrority}
                    </td>
                    <td className="py-4 px-4 text-gray-700">
                      {operator.paymentType == 1 ? operator.balance:''}
                    </td>
                    <td className="py-4 px-4 text-gray-700">
                        {operator.paymentType == 1 ? operator.currencySymbol+" "+operator.balance+"/"+operator.creditLimit:''}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <span className={`${operator.status === 'Active' ? 'text-green-600' : 'text-orange-600'} font-medium`}>
                          {operator.status}
                        </span>
                       
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        
                        { (
                          <button
                            onClick={() => {
                              setSelectedOperator(operator);
                              setView('topup');
                            }}
                            className="bg-red-500 text-white text-xs px-2 py-1 rounded cursor-pointer"
                          >
                            Top Up
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-800"><a href={'/webapi/qrcode?Userpk='+encodeURIComponent(sqids.encode([operator?.userPk]))} target="_blank" rel="noopener noreferrer">view</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

  
    
  
  
  if (view === 'list') {
    return <OperatorsList />;
  } else if (view === 'detail') {
    return <OperatorDetail 
      selectedOperator={selectedOperator}
      setSelectedOperator={setSelectedOperator}
      setView={setView}
      
    />;
  } else {
    return <OperatorTopUp selectedOperator={selectedOperator}
    setSelectedOperator={setSelectedOperator}
    setView={setView}
    />;
  }
};



export default OperatorsManagement;
